import { Link } from "react-router-dom";
import PageLayout from "@/components/aoria/PageLayout";
import PageHeader from "@/components/aoria/PageHeader";
import Seo from "@/components/aoria/Seo";

/**
 * Page Enjeux — socle intellectuel d'Aoria.
 * Quatre thèses fondatrices, structurées comme une argumentation continue.
 */
const THESES = [
    {
        n: "I.",
        eyebrow: "Définir",
        title: "Qu'appelle-t-on accessibilité cognitive ?",
        body: [
            "L'accessibilité cognitive désigne la facilité avec laquelle une personne peut comprendre, traiter et utiliser une information donnée. Elle ne se réduit ni à la lecture, ni à la mémoire, ni à l'attention prise isolément : elle combine ces dimensions, et beaucoup d'autres, dans la vie ordinaire.",
            "Comme l'accessibilité physique (rampes, ascenseurs, sous-titres), elle relève d'une conception qui bénéficie à tout le monde. Un texte clair, une consigne lisible, une procédure explicite : ce sont des aménagements pour quelques-uns qui deviennent des conforts pour tous.",
        ],
    },
    {
        n: "II.",
        eyebrow: "Constater",
        title:
            "Pourquoi certaines personnes rencontrent davantage de difficultés avec les contenus numériques",
        body: [
            "Les contenus numériques imposent souvent une charge cognitive importante : densité d'information, navigation implicite, vocabulaire technique, consignes ambiguës, sollicitations parallèles. Pour des personnes ayant des troubles des apprentissages, une situation de fatigue, un stress prolongé ou un vieillissement, ces obstacles peuvent devenir bloquants.",
            "Ces difficultés ne concernent pas une minorité résiduelle. Elles traversent la vie de chacun, à un moment ou à un autre. Les ignorer revient à exclure une part importante de la population de l'accès réel aux services et aux savoirs.",
        ],
    },
    {
        n: "III.",
        eyebrow: "Identifier un levier",
        title:
            "Pourquoi l'intelligence artificielle peut devenir un outil d'accessibilité",
        body: [
            "Reformuler une consigne, simplifier un document, traduire un jargon, structurer une démarche, hiérarchiser des informations : ces opérations cognitives demandent un effort. Les outils d'intelligence artificielle, bien utilisés, en absorbent une partie.",
            "L'enjeu n'est pas de déléguer la pensée, mais de réduire la pénibilité d'opérations annexes qui se mettent entre la personne et ce qu'elle veut comprendre, dire ou faire. Bien encadrée, cette assistance peut redonner de la latitude là où des obstacles cognitifs en avaient ôté.",
        ],
    },
    {
        n: "IV.",
        eyebrow: "Avertir",
        title:
            "Pourquoi un risque de nouvelle fracture cognitive existe, et comment ne pas l'aggraver",
        body: [
            "Si les usages restent confidentiels, si la maîtrise de ces outils devient un savoir-faire de niche, si les institutions les interdisent par principe sans rien proposer en retour, alors une nouvelle fracture cognitive s'installera : ceux qui savent en tirer parti, et ceux qui resteront sur le bord du chemin.",
            "Aoria existe pour rendre lisibles ces enjeux, documenter les usages raisonnés et défendre une diffusion équitable de ces outils. L'accessibilité cognitive est, à nos yeux, une condition d'équité, pas un avantage particulier.",
        ],
    },
];

export default function Enjeu() {
    return (
        <PageLayout testid="enjeux-page">
            <Seo
                title="Enjeux · L'accessibilité cognitive à l'ère numérique · Aoria"
                description="Quatre thèses fondatrices d'Aoria sur l'accessibilité cognitive à l'ère numérique : définir, constater, identifier les leviers et avertir du risque d'une fracture cognitive."
                path="/enjeu"
            />
            <PageHeader
                testid="enjeux"
                eyebrow="ENJEUX"
                title="L'accessibilité cognitive"
                italic="est l'angle mort de la transformation numérique"
                lead="Quatre thèses pour situer le projet d'Aoria. Elles forment le socle à partir duquel se déploient nos publications, nos partenariats et nos prises de position."
                backdrop="weave"
            />

            <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-24 md:pb-32">
                <ol className="divide-y divide-[#E0E1DD] border-y border-[#E0E1DD]">
                    {THESES.map((t, i) => (
                        <li
                            key={t.n}
                            data-testid={`thesis-${i}`}
                            className="py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12"
                        >
                            <div className="md:col-span-3">
                                <div className="editorial-num text-3xl md:text-4xl text-[#0D1B2A]">
                                    {t.n}
                                </div>
                                <div className="hairline mt-4 pb-3" />
                                <span className="eyebrow mt-4 block">
                                    {t.eyebrow}
                                </span>
                            </div>
                            <div className="md:col-span-9 max-w-3xl">
                                <h2 className="h2 text-2xl md:text-3xl lg:text-[2.25rem] text-[#0D1B2A] leading-snug">
                                    {t.title}
                                </h2>
                                <div className="mt-7 space-y-5">
                                    {t.body.map((p) => (
                                        <p
                                            key={p}
                                            className="text-[#1B263B] text-base md:text-lg leading-relaxed"
                                        >
                                            {p}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </li>
                    ))}
                </ol>

                {/* « À retenir », synthèse en 4 idées-clés */}
                <aside
                    data-testid="enjeux-a-retenir"
                    className="mt-20 md:mt-28 border border-[#0D1B2A]/15 bg-[#F4F5F7] p-8 md:p-12"
                >
                    <div className="flex items-center gap-4 mb-8">
                        <span
                            aria-hidden="true"
                            className="inline-block h-px w-9 bg-[#0D1B2A]/40"
                        />
                        <span className="eyebrow">À retenir</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                        {[
                            "L'accessibilité cognitive ne concerne pas uniquement le handicap.",
                            "Les difficultés de compréhension peuvent toucher chacun à certains moments.",
                            "L'intelligence artificielle peut réduire certains obstacles cognitifs.",
                            "Une nouvelle fracture cognitive peut apparaître si ces outils restent réservés à une minorité.",
                        ].map((idea, i) => (
                            <div
                                key={idea}
                                data-testid={`enjeux-a-retenir-${i}`}
                                className="flex gap-5"
                            >
                                <span className="editorial-num text-sm shrink-0 pt-1">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <p className="font-serif text-lg md:text-xl leading-snug text-[#0D1B2A]">
                                    {idea}
                                </p>
                            </div>
                        ))}
                    </div>
                </aside>

                <aside
                    data-testid="enjeux-pullquote"
                    className="mt-24 md:mt-32 max-w-4xl mx-auto text-center"
                >
                    <p className="font-serif italic text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-[#0D1B2A]">
                        « Ce qui est une difficulté pour quelques-uns est, à un
                        moment ou à un autre, une réalité pour presque tous. »
                    </p>
                    <div className="eyebrow mt-8">Simon Marie, président d'Aoria</div>
                </aside>

                <div className="mt-24 md:mt-32 flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                        to="/outils"
                        data-testid="enjeux-cta-outils"
                        className="btn-primary rounded-full inline-block px-7 py-3.5 text-sm tracking-[0.15em] uppercase"
                    >
                        Voir les outils
                    </Link>
                    <Link
                        to="/manifeste"
                        data-testid="enjeux-cta-manifeste"
                        className="btn-secondary rounded-full inline-block px-7 py-3.5 text-sm tracking-[0.15em] uppercase"
                    >
                        Lire le manifeste
                    </Link>
                </div>
            </section>
        </PageLayout>
    );
}
